import useRewardPolicies from '@hooks/useRewardPolicies';
import { OrderLine } from '@models/order';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { getCouponUnitCount } from '@utils/couponUtils';
import { makeItemKey } from '@utils/orderUtils';
import { useMemo } from 'react';

function useWillEarnStamp() {
    const { orderLines, discounts, giftPayments } = useCalculatedRequest();
    const { stampEnabled } = useRewardPolicies();

    return useMemo(() => {
        // 스탬프 정책 사용 여부 판단
        if (!stampEnabled) {
            return 0;
        }
        // 주문 쿠폰 적용 시 0개 적립
        const orderCouponDiscounted = !!discounts
            ?.find((it) => it.type === 'COUPON'
                && it.discountScope === 'ORDER');

        // 선물 사용 시 0개 적립
        const giftDiscounted = (giftPayments?.length || 0) !== 0;

        const employeeDiscounted = !!discounts
            ?.find((it) => it.type === 'EMPLOYEE');

        if (orderCouponDiscounted || giftDiscounted || employeeDiscounted) {
            return 0;
        }
        const itemCoupons = discounts
            ?.filter((it) => it.type === 'COUPON' && it.discountScope === 'ITEM') || [];

        return orderLines
            // 품절·단종 상품은 결제 금액에 포함되지 않으므로 적립 대상도 아니다
            .filter((line) => line.status === 'ON_SALE')
            .map((line) => {
                // 쿠폰이 차지한 수량만큼은 적립되지 않는다. 1+1 쿠폰은 상품 2개를 차지한다
                const couponUnits = itemCoupons
                    .filter((coupon) => coupon.itemKey === makeItemKey(line))
                    .reduce((acc, coupon) => acc + getCouponUnitCount(coupon), 0);
                const rewardableQuantity = Math.max(line.quantity - couponUnits, 0);
                return getUnitStampCount(line) * rewardableQuantity;
            })
            .reduce((acc, cur) => acc + cur, 0);
    }, [orderLines, stampEnabled, discounts, giftPayments]);
}

/** 상품 1개당 적립되는 스탬프 수. 상품과 옵션의 적립 정책을 각각 더한다 */
function getUnitStampCount(line: OrderLine) {
    const {
        rewardAmount, rewardType, defaultOptions, personalOptions,
    } = line;
    const options = [...defaultOptions, ...personalOptions]
        .map((it) => it.options)
        .flat();

    const itemRewardAmount = rewardType === 'STAMP' ? rewardAmount : 0;
    const optionRewardAmount = options
        ?.filter((option) => option.rewardType === 'STAMP')
        ?.map((option) => option.rewardAmount * option.quantity)
        ?.reduce((acc, cur) => acc + cur, 0) || 0;
    return itemRewardAmount + optionRewardAmount;
}

export default useWillEarnStamp;
